/**
 * useChartData - Data Processing Hook
 * 
 * SOLID Principle: Single Responsibility Principle (SRP)
 * Purpose: Handles all data processing logic for chart visualization.
 * Separates data processing concerns from UI rendering and state management.
 * 
 * Responsibilities:
 * - Process raw sensor data through ChartDataProcessor
 * - Handle both individual and merged mode data processing
 * - Manage data state updates and tracking
 * - Configure zoom controller with processed data
 */

import { useEffect, useState, useRef } from 'react';
import { ChartDataProcessor } from '../ChartDataProcessor.js';
import { useDiagnostics } from '../../../diagnostics/DiagnosticsContext';

export function useChartData({
  rawData,
  selectedFields,
  perBuoySelectedFields,
  selectedBuoys,
  mergedMode
}) {
  const { badDataBin } = useDiagnostics();
  const [chartData, setChartData] = useState([]);
  const [hasEverHadData, setHasEverHadData] = useState(false);
  const [previousChartData, setPreviousChartData] = useState([]);
  const originalDataRef = useRef([]);

  // === Data Processing ===
  useEffect(() => {
    let processed;
    
    if (mergedMode && selectedBuoys.length > 0) {
      // In merged mode, process each buoy's data separately then combine
      processed = ChartDataProcessor.processChartDataMerged(
        rawData,
        perBuoySelectedFields,
        selectedBuoys,
        badDataBin
      );
    } else {
      // Individual mode - use existing logic
      processed = ChartDataProcessor.processChartData(
        rawData, 
        selectedFields, 
        badDataBin,
        false
      );
    }
    
    setChartData(processed.chartData);
    originalDataRef.current = processed.chartData;
    
    // Track if we've ever had data to show and store previous data
    if (processed.chartData.length > 0) {
      setHasEverHadData(true);
      setPreviousChartData(processed.chartData);
    }
    
  }, [rawData, selectedFields?.join('|'), JSON.stringify(perBuoySelectedFields), badDataBin, selectedBuoys?.join('|'), mergedMode]);

  return {
    chartData,
    setChartData,
    hasEverHadData,
    previousChartData,
    originalDataRef
  };
}

export default useChartData;